/* FILE: ./frontend/src/features/products/ProductDetail.jsx */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

export default function ProductDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const { user } = useAuth();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(`${API_BASE}/products/${id}`, {
          headers: user?.id ? { 'x-user-id': user.id } : {}
        });
        setProduct(res.data);
      } catch (err) {
        console.error(err);
        setError(t('product_detail.not_found'));
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, user, t]);

  if (loading) return <div className="p-12 text-center animate-pulse">{t('product_detail.loading')}</div>;

  if (error || !product) return <div className="p-12 text-center text-red-600 font-bold">{error || t('product_detail.not_found')}</div>;

  // ⚡ COMPARES LOGGED-IN USER ID WITH SUPPLIER'S USER_ID
  const isOwner = user && product.suppliers?.user_id && String(product.suppliers.user_id) === String(user.id);

  // ⚡ OPTION A FOR RFQ: Route directly to /rfq/new with preselectedSupplier & product details
  const handleRequestQuote = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    navigate('/rfq/new', {
      state: {
        preselectedSupplier: product.suppliers,
        autoFillCode: product.hs_code,
        materialName: product.name
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        
        {/* Navigation Breadcrumb */}
        <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-blue-600 mb-6 flex items-center gap-1">
          {t('product_detail.back_dir')}
        </button>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          {/* HEADER SECTION */}
          <div className="bg-slate-900 text-white p-8">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-3 mb-2">
                   <span className="bg-blue-600 text-xs font-bold px-2 py-1 rounded">{t('product_detail.hs')} {product.hs_code}</span>
                   {product.sku && <span className="bg-slate-700 text-xs font-mono px-2 py-1 rounded">{t('product_detail.sku')} {product.sku}</span>}
                </div>
                <h1 className="text-3xl font-bold">{product.name}</h1>
                <p className="text-slate-300 mt-2 text-lg">
                  {t('product_detail.supplied_by')} {product.suppliers?.company_name} 
                  {product.suppliers?.city && product.suppliers?.province 
                    ? ` • ${product.suppliers.city}, ${product.suppliers.province}, ${t('product_detail.canada')}` 
                    : ` • ${t('product_detail.canada')}`}
                </p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">${product.price_cad} <span className="text-sm text-slate-400 font-normal">{t('product_detail.cad_per')} {product.uom}</span></p>
                <p className="text-sm text-slate-400 mt-1">{t('product_detail.excl_tax')}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3">
            
            {/* LEFT: SPECS & DESC */}
            <div className="lg:col-span-2 p-8 border-r border-gray-100">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">{t('product_detail.tech_specs')}</h3>
              <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                {product.description}
              </p>

              <div className="mt-8 grid grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                   <span className="block text-xs font-bold text-gray-400 uppercase">{t('product_detail.certs')}</span>
                   <span className="font-medium text-gray-800">{product.certification_claimed || t('product_detail.none')}</span>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                   <span className="block text-xs font-bold text-gray-400 uppercase">{t('product_detail.origin')}</span>
                   <span className="font-medium text-gray-800">
                     {t('product_detail.made_in')} {product.origin_province || product.suppliers?.province || t('product_detail.canada')}
                   </span>
                </div>
              </div>

              {/* Compliance Badges */}
              <div className="mt-8 flex gap-3">
                 <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold flex items-center">
                   {t('product_detail.civ_use')}
                 </span>
                 <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold flex items-center">
                   {t('product_detail.cfta_eligible')}
                 </span>
              </div>
            </div>

            {/* RIGHT: LOGISTICS & ACTION */}
            <div className="p-8 bg-gray-50/50">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-6">{t('product_detail.logistics')}</h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-medium">{t('product_detail.avail')}</span>
                  {/* ⚡ FIXED: Added explicit styling for DRAFT status */}
                  <span className={`font-bold px-2 py-1 rounded text-xs uppercase ${
                      product.stock_status === 'in_stock' ? 'bg-green-100 text-green-800' : 
                      product.stock_status === 'draft' ? 'bg-amber-100 text-amber-800' :
                      'bg-yellow-100 text-yellow-800'
                  }`}>
                    {product.stock_status?.replace('_', ' ')}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-medium">{t('product_detail.lead_time')}</span>
                  <span className="font-bold text-gray-900">{product.lead_time_days} {t('product_detail.days')}</span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-medium">{t('product_detail.moq')}</span>
                  <span className="font-bold text-gray-900">{product.moq} {product.uom}</span>
                </div>
              </div>

              <div className="mt-8">
                {/* ⚡ FIXED: Prevent Self-Quoting & Route to /rfq/new (Option A) */}
                {!isOwner ? (
                    <button 
                      onClick={handleRequestQuote}
                      className="w-full py-4 rounded-xl font-bold shadow-lg transition-transform transform hover:-translate-y-0.5 bg-blue-600 text-white hover:bg-blue-700"
                    >
                      {t('product_detail.req_quote')}
                    </button>
                ) : (
                    <div className="space-y-3">
                        <button 
                            onClick={() => navigate('/inventory')} 
                            className="w-full py-4 rounded-xl font-bold border-2 border-indigo-600 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                        >
                           {t('product_detail.edit_listing')}
                        </button>
                        {product.stock_status === 'draft' && (
                             <p className="text-center text-xs text-amber-600 font-bold">{t('product_detail.draft_notice')}</p>
                        )}
                    </div>
                )}
                {!isOwner && (
                    <p className="text-center text-xs text-gray-400 mt-3">
                      {t('product_detail.direct_inquiry')} {product.suppliers?.company_name}
                    </p>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}